import React, { useContext, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';

import { faSyncAlt } from '@fortawesome/pro-light-svg-icons';
import { Alert, Snackbar } from '@mui/material';

import { AppDispatch } from '../../../../app/store';
import { isAdmin, isManager } from '../../../../common/actionHelper';
import { manualTriggerClrEndPoint } from '../../../../common/api.endPoints';
import { IconLink } from '../../../../common/components/IconLink';
import { FeatureFlags } from '../../../../common/constants/featureFlags';
import { NoraPermissionV2 } from '../../../../common/constants/roles';
import { FeatureFlagsContext } from '../../../../common/featureFlagsWrapper';
import { useAccess } from '../../../../common/hooks/useAccess';
import { HttpService } from '../../../../common/http.service';
import { syncApplicationState } from '../../../detail/detailSlice';

export const TriggerClr = () => {
  const { flags } = useContext(FeatureFlagsContext);
  const dispatch = useDispatch<AppDispatch>();
  const { id } = useParams<{ id: string }>();

  const canTrigger = useAccess({
    permission: NoraPermissionV2.TRIGGER_CLR,
    legacyCheck: roles => isAdmin(roles) || isManager(roles),
  });

  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const isHaltClrEnabled = flags[FeatureFlags.UW_NORA_HALT_CLR_ENABLED] || false;

  // Don't render if feature is disabled, user doesn't have permission, or no application id
  if (!isHaltClrEnabled || !canTrigger || !id) {
    return null;
  }

  const handleClick = async () => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      const response = await HttpService.getInstance().post(
        manualTriggerClrEndPoint(id),
        {},
      );

      if (response && !response.error) {
        dispatch(syncApplicationState(id));
        setSnackbar({
          open: true,
          message: 'Application successfully routed to Closed Loop Rescission',
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: response?.error?.message || 'Not eligible for CLR',
          severity: 'error',
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to trigger CLR. Please try again.',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <>
      <IconLink
        onClick={handleClick}
        icon={faSyncAlt}
        copy={isLoading ? 'Processing...' : 'Trigger CLR'}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ marginTop: '40px' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%', minWidth: '400px' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TriggerClr;
